import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/motion";
import { useLang } from "@/context/LangContext";
import { useSite } from "@/context/SiteContext";
import { PageLayout } from "@/components/PageLayout";

const AMOUNTS = [10, 25, 50, 100];
const FUNDS = ["tithe", "offering", "radio"];

export default function Donate() {
  const { t } = useLang();
  const { settings, on } = useSite();
  const terms = on("footer_terms");
  const enabled = settings?.donate_enabled !== false;
  const [fund, setFund] = useState("tithe");
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const value = amount === "custom" ? Number(custom) || 0 : amount;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const give = async () => {
    if (value < 1) {
      toast.error(t("donate.min"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { fund, amount: value, name, email, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <main data-testid="donate-page">
      <PageLayout page="donate" sections={{
        hero: <PageHero testid="donate-hero" over={t("donate.over")} title={t("donate.title")} lead={t("donate.verse")} imgKey="donate_hero" />,
        form: (
      <section className="mx-auto max-w-[1400px] px-5 pb-28 md:px-10">
        <FadeUp className="mx-auto max-w-3xl rounded-[32px] border border-[#D4AF37]/20 bg-[#131C2E] p-6 md:p-12">
          <p className="font-ui text-sm font-semibold text-[#F4EEE2]">{t("donate.fund")}</p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {FUNDS.map((f) => (
              <button
                key={f}
                data-testid={`donate-fund-${f}`}
                onClick={() => setFund(f)}
                className={`relative rounded-2xl border px-4 py-5 text-left font-display text-2xl transition-colors duration-300 ${fund === f ? "border-[#D4AF37] text-[#F4EEE2]" : "border-[#F4EEE2]/10 text-[#C9D0DB] hover:border-[#F4EEE2]/30"}`}
              >
                {fund === f && <motion.span layoutId="fund-bg" className="absolute inset-0 rounded-2xl bg-[#D4AF37]/10" />}
                <span className="relative">{t(`donate.${f}`)}</span>
              </button>
            ))}
          </div>

          <p className="mt-10 font-ui text-sm font-semibold text-[#F4EEE2]">{t("donate.amount")}</p>
          <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5">
            {[...AMOUNTS, "custom"].map((a) => (
              <button
                key={a}
                data-testid={`donate-amount-btn-${a}`}
                onClick={() => setAmount(a)}
                className={`rounded-full py-3 font-ui text-base font-semibold transition-colors duration-300 ${amount === a ? "bg-[#D4AF37] text-[#0B111E]" : "bg-[#0B111E] text-[#F4EEE2] hover:bg-[#1C2D42]"}`}
              >
                {a === "custom" ? t("donate.custom") : `$${a}`}
              </button>
            ))}
          </div>
          {amount === "custom" && (
            <input
              data-testid="donate-custom-input"
              type="number"
              min="1"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="$"
              className="mt-4 w-full rounded-full border border-[#F4EEE2]/15 bg-[#0B111E] px-5 py-3 text-[#F4EEE2] outline-none focus:border-[#D4AF37]"
            />
          )}

          {enabled && (
            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              <p className="font-ui text-sm font-semibold text-[#F4EEE2] sm:col-span-2">{t("donate.receipt")}</p>
              <input data-testid="donate-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("donate.name")} className="w-full rounded-full border border-[#F4EEE2]/15 bg-[#0B111E] px-5 py-3 text-[#F4EEE2] outline-none placeholder:text-[#9AA4B4] focus:border-[#D4AF37]" />
              <input data-testid="donate-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("donate.email")} className="w-full rounded-full border border-[#F4EEE2]/15 bg-[#0B111E] px-5 py-3 text-[#F4EEE2] outline-none placeholder:text-[#9AA4B4] focus:border-[#D4AF37]" />
              <p className="flex flex-wrap gap-x-5 gap-y-1 font-ui text-xs text-[#B7BFCC] sm:col-span-2">
                <Link to="/p/privacy-policy" data-testid="donate-privacy-link" className="underline underline-offset-4 hover:text-[#D4AF37]">{t("donate.privacy")}</Link>
                {terms && <Link to="/p/terms-of-giving" data-testid="donate-terms-link" className="underline underline-offset-4 hover:text-[#D4AF37]">{t("donate.terms")}</Link>}
              </p>
            </div>
          )}

          {enabled ? (
          <button
            data-testid="donate-submit-btn"
            disabled={busy}
            onClick={give}
            className="mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-[#F4EEE2] py-5 font-ui text-lg font-semibold text-[#0B111E] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#D4AF37]"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Heart className="h-5 w-5" />} {t("donate.cta")} {value ? `$${value}` : ""} · {t(`donate.${fund}`)}
          </button>
          ) : (
            <p data-testid="donate-disabled-msg" className="mt-10 rounded-full border border-[#F4EEE2]/15 py-5 text-center font-ui text-sm text-[#C9D0DB]">{t("donate.soon")}</p>
          )}
          {enabled && terms && (
            <p data-testid="donate-agree-note" className="mt-4 text-center font-ui text-xs text-[#B7BFCC]">
              {t("donate.agree")} <Link to="/p/terms-of-giving" data-testid="donate-agree-terms-link" className="underline underline-offset-4 hover:text-[#D4AF37]">{t("donate.terms")}</Link>.
            </p>
          )}
          <p className="mt-4 flex items-center justify-center gap-2 font-ui text-xs text-[#B7BFCC]">
            <Lock className="h-3.5 w-3.5" /> {t("donate.secure")}
          </p>
        </FadeUp>
      </section>
        ),
      }} />
    </main>
  );
}
